/**
 * Where a diner's cart secret lives between requests (ORDR-42, COMM-132).
 *
 * paio-commerce returns `cart_secret` ONCE, on the 201 that opens a FOOD cart,
 * and every later cart, quote and checkout call presents it as
 * `Authorization: Cart <cart_secret>`. It is authority over that cart, so:
 * - sessionStorage only, keyed per cart: it dies with the tab;
 * - never logged, never in a URL, never in an error message;
 * - cleared once checkout has produced an order.
 *
 * Storage can be absent or throw, so every access is guarded. An in-memory
 * copy keeps the tab working when storage refuses; a reload then starts a new
 * cart, which is the honest outcome.
 */

const PREFIX = "paio-ordering-ui:cart-secret:";

export interface CartSecretStore {
  read(cartId: string): string | null;
  write(cartId: string, secret: string): void;
  clear(cartId: string): void;
}

function sessionStore(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function createCartSecretStore(backing: () => Storage | null = sessionStore): CartSecretStore {
  const memory = new Map<string, string>();
  return {
    read(cartId) {
      try {
        const stored = backing()?.getItem(PREFIX + cartId) ?? null;
        if (stored !== null) {
          return stored;
        }
      } catch {
        // Fall through to the in-memory copy.
      }
      return memory.get(cartId) ?? null;
    },
    write(cartId, secret) {
      memory.set(cartId, secret);
      try {
        backing()?.setItem(PREFIX + cartId, secret);
      } catch {
        // The in-memory copy stands.
      }
    },
    clear(cartId) {
      memory.delete(cartId);
      try {
        backing()?.removeItem(PREFIX + cartId);
      } catch {
        // Nothing stored that could be removed.
      }
    },
  };
}
